import { getLanguageKeyForFilePath } from '../../language-registry.js';
import { parseSourceRoot } from '../../parser-runtime.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from '../../yield-during-work.js';
import { yieldToEventLoop } from '../../../runtime/yield-to-event-loop.js';
import type { SymbolImportBinding } from '../../types.js';
import { extractCIncludeBindings } from './c.js';
import { extractCppImportBindings } from './cpp.js';
import { extractEcmaScriptBindings } from './ecma-script.js';
import { extractGoImportBindings } from './go.js';
import { extractJavaImportBindings } from './java.js';
import { extractPythonImportBindings } from './python.js';
import { extractRubyImportBindings } from './ruby.js';
import { extractRustImportBindings } from './rust.js';

type ImportExtractor = (
  root: import('web-tree-sitter').SyntaxNode,
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
) => SymbolImportBinding[];

const EXTRACTORS_BY_LANGUAGE_KEY: Readonly<Record<string, ImportExtractor>> = {
  javascript: extractEcmaScriptBindings,
  typescript: extractEcmaScriptBindings,
  tsx: extractEcmaScriptBindings,
  python: extractPythonImportBindings,
  go: extractGoImportBindings,
  rust: extractRustImportBindings,
  ruby: extractRubyImportBindings,
  java: extractJavaImportBindings,
  c: extractCIncludeBindings,
  cpp: extractCppImportBindings,
};

export async function extractImportBindingsFromSource(
  filePath: string,
  source: string,
  workspaceFiles: ReadonlySet<string>,
): Promise<SymbolImportBinding[]> {
  const languageKey = getLanguageKeyForFilePath(filePath);
  if (!languageKey) return [];

  const extract = EXTRACTORS_BY_LANGUAGE_KEY[languageKey];
  if (!extract) return [];

  const parsed = await parseSourceRoot(filePath, source);
  if (!parsed) return [];
  try {
    await yieldToEventLoop();
    const yieldState = createCodeNavYieldState();
    await yieldDuringCodeNavWork(yieldState);
    const bindings = extract(parsed.root, filePath, workspaceFiles);
    await yieldDuringCodeNavWork(yieldState);
    return bindings;
  } finally {
    parsed.cleanup();
  }
}
